import * as React from 'react';
import { X, CheckCircle, AlertCircle, InfoIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ToastContext, type ToastType } from './toast';

const DEFAULT_DURATION = 3000;

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose?: () => void;
  duration?: number;
}

export const Toast = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & ToastProps
>(
  (
    {
      className,
      message,
      type = 'info',
      onClose,
      duration = DEFAULT_DURATION,
      ...props
    },
    ref,
  ) => {
    React.useEffect(() => {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, duration);
      return () => clearTimeout(timer);
    }, [duration, onClose]);

    const icons = {
      success: <CheckCircle className="h-5 w-5 text-green-500" />,
      error: <AlertCircle className="h-5 w-5 text-red-500" />,
      info: <InfoIcon className="h-5 w-5 text-blue-500" />,
    };

    const bgColors = {
      success: 'bg-green-50 border-green-200',
      error: 'bg-red-50 border-red-200',
      info: 'bg-blue-50 border-blue-200',
    };

    const textColors = {
      success: 'text-green-800',
      error: 'text-red-800',
      info: 'text-blue-800',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'fixed top-10 left-1/2 -translate-x-1/2 p-4 rounded-md shadow-md border z-50 flex items-center gap-3 max-w-md transform transition-all animate-in fade-in slide-in-from-top-5',
          bgColors[type],
          className,
        )}
        {...props}
      >
        {icons[type]}
        <p className={cn('text-sm flex-grow', textColors[type])}>{message}</p>
        <button
          onClick={onClose}
          className={cn('rounded-full p-1 hover:bg-white/20', textColors[type])}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  },
);

Toast.displayName = 'Toast';

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = React.useState<{
    visible: boolean;
    message: string;
    type: ToastType;
    duration: number;
  }>({
    visible: false,
    message: '',
    type: 'info',
    duration: DEFAULT_DURATION,
  });

  const showToast = React.useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = DEFAULT_DURATION,
    ) => {
      setToast({ visible: true, message, type, duration });
    },
    [],
  );

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={toast.duration}
        />
      )}
    </ToastContext.Provider>
  );
};
